import { ControlWithFieldsView } from "api/generated/model";
import { SpreadsheetRow } from "utils/spreadsheet/types";

import { validateControlRows, ValidationContext } from "./controlValidator";
import { convertToControlGraphs } from "./converter";
import { parseSchema } from "./schemaParser";
import { ImportedControlDefaults, ValidControlRow } from "./types";

export type ProcessSpreadsheetResult =
    | {
          success: true;
          controlGraphs: ControlWithFieldsView[];
          validRows: ValidControlRow[];
      }
    | { success: false; errors: string[] };

/**
 * Processes spreadsheet data through the complete pipeline:
 * 1. Parse spreadsheet data
 * 2. Validate import rows
 * 3. Convert to control graphs
 *
 * @param rows Raw spreadsheet data
 * @param validationContext Context for validating rows against existing data
 * @param defaults Values for the fields the spreadsheet has no column for
 * @returns Process result with either control graphs or error messages
 */
export function processSpreadsheet(
    rows: SpreadsheetRow[],
    validationContext: ValidationContext,
    defaults: ImportedControlDefaults
): ProcessSpreadsheetResult {
    if (!rows || rows.length === 0) {
        return {
            success: false,
            errors: ["No controls data provided for bulk import"],
        };
    }

    const parseResult = parseSchema(rows);
    if (!parseResult.success) {
        return { success: false, errors: parseResult.errors };
    }

    const validationResult = validateControlRows(
        parseResult.data,
        validationContext
    );
    if (!validationResult.success) {
        return { success: false, errors: validationResult.errors };
    }

    const controlGraphs = convertToControlGraphs(
        validationResult.data,
        defaults
    );

    if (!controlGraphs || controlGraphs.length === 0) {
        return {
            success: false,
            errors: ["Failed to bulk import controls from the provided data"],
        };
    }

    return {
        success: true,
        controlGraphs,
        validRows: validationResult.data,
    };
}
